<div class="billing-container">
  <!-- ───────────── Hero header ───────────── -->
  <div class="billing-header">
    <div class="header-content">
      <div class="header-left">
        <i class="fa-solid fa-file-invoice-dollar"></i>
        <div class="header-text">
          <h1>Facturación del Arrendamiento</h1>
          <p class="header-subtitle">Datos para emitir el reporte de Facturas Pendientes (CFDI)</p>
        </div>
      </div>
      <div class="header-right">
        <p-tag *ngIf="mode === 'edit'" value="Configurada" severity="success" icon="pi pi-check-circle"></p-tag>
        <p-tag *ngIf="mode === 'create'" value="Sin configurar" severity="warning" icon="pi pi-clock"></p-tag>
      </div>
    </div>
  </div>

  <form [formGroup]="billingForm" (ngSubmit)="onFormSubmit()">
    <div class="billing-grid">
      <!-- ───────────── Resumen fiscal (read-only, derivado) ───────────── -->
      <div class="info-card">
        <div class="card-header">
          <i class="fa-solid fa-id-card text-blue-600"></i>
          <h3>Resumen fiscal</h3>
        </div>
        <div class="card-content">
          <p class="section-note">
            Estos datos se toman automáticamente del cliente y del arrendamiento (no se editan aquí).
          </p>

          <div class="compact-grid">
            <div class="compact-item full-width">
              <span class="label">Razón Social <small>(cliente)</small></span>
              <span class="value">{{ fiscalSummary?.razonSocial || '—' }}</span>
            </div>
            <div class="compact-item">
              <span class="label">RFC</span>
              <span class="value mono">{{ fiscalSummary?.rfc || '—' }}</span>
            </div>
            <div class="compact-item">
              <span class="label">No. ID</span>
              <span class="value mono">{{ fiscalSummary?.oldId || '—' }}</span>
            </div>
            <div class="compact-item full-width">
              <span class="label">No. de Contrato <small>(pestaña Contrato)</small></span>
              <span class="value" *ngIf="fiscalSummary?.contractNumber">{{ fiscalSummary?.contractNumber }}</span>
              <span class="value value-warn" *ngIf="!fiscalSummary?.contractNumber">— · captúralo en la pestaña Contrato</span>
            </div>
          </div>

          <div class="summary-line">
            <span><i class="fa-solid fa-list-ol"></i> Conceptos: <strong>{{ conceptosCount }}</strong></span>
            <span><i class="fa-solid fa-coins"></i> Total sin IVA: <strong>{{ conceptosTotal | currency:'MXN':'symbol':'1.2-2' }}</strong></span>
          </div>

          <div class="fixed-values">
            <span class="fixed-title">Valores fijos del reporte</span>
            <span class="fixed-chip">Serie A</span>
            <span class="fixed-chip">Sucursal Almacén</span>
            <span class="fixed-chip">IVA 16%</span>
            <span class="fixed-chip">Peso mexicano</span>
            <span class="fixed-chip">Tipo de cambio 1</span>
          </div>
        </div>
      </div>

      <!-- ───────────── Datos de facturación (editables) ───────────── -->
      <div class="info-card">
        <div class="card-header">
          <i class="fa-solid fa-file-invoice text-blue-600"></i>
          <h3>Datos de facturación</h3>
        </div>
        <div class="card-content">
          <div class="field field-flex">
            <label for="paymentMethod">*Forma de pago:</label>
            <p-dropdown inputId="paymentMethod" [options]="paymentMethods" formControlName="paymentMethod"
              optionLabel="label" optionValue="value" [filter]="true" filterBy="label"
              placeholder="Selecciona la forma de pago" appendTo="body"
              [class.ng-invalid]="hasError('paymentMethod', 'required')"></p-dropdown>
            <small class="p-error" *ngIf="hasError('paymentMethod', 'required')">{{ getErrorMessage('paymentMethod') }}</small>
          </div>

          <div class="field field-flex">
            <label for="paymentType">*Método de pago:</label>
            <p-dropdown inputId="paymentType" [options]="paymentTypes" formControlName="paymentType"
              optionLabel="label" optionValue="value" placeholder="Selecciona el método de pago" appendTo="body"
              [class.ng-invalid]="hasError('paymentType', 'required')"></p-dropdown>
            <small class="p-error" *ngIf="hasError('paymentType', 'required')">{{ getErrorMessage('paymentType') }}</small>
          </div>

          <div class="field field-flex">
            <label for="cfdiUsage">*Uso de CFDI:</label>
            <p-dropdown inputId="cfdiUsage" [options]="cfdiUsages" formControlName="cfdiUsage"
              optionLabel="label" optionValue="value" [filter]="true" filterBy="label"
              placeholder="Selecciona el uso de CFDI" appendTo="body"
              [class.ng-invalid]="hasError('cfdiUsage', 'required')"></p-dropdown>
            <small class="p-error" *ngIf="hasError('cfdiUsage', 'required')">{{ getErrorMessage('cfdiUsage') }}</small>
          </div>
        </div>
      </div>
    </div>

    <!-- ───────────── Conceptos a facturar ───────────── -->
    <div class="info-card wide-card conceptos-card">
      <div class="card-header">
        <i class="fa-solid fa-list-ol text-green-600"></i>
        <h3>Conceptos a facturar</h3>
        <div class="header-tags">
          <button pButton type="button" label="Agregar concepto" icon="fa-solid fa-plus"
            class="p-button-sm p-button-outlined" (click)="addConcepto()"></button>
        </div>
      </div>
      <div class="card-content">
        <p class="conceptos-empty" *ngIf="conceptos.length === 0">
          Sin conceptos. Agrega al menos uno para que este arrendamiento aparezca en el reporte de Facturas Pendientes.
        </p>

        <div class="conceptos-table" *ngIf="conceptos.length > 0" formArrayName="conceptos">
          <div class="concepto-row concepto-row--head">
            <span class="c-pos">#</span>
            <span class="c-qty">Cantidad</span>
            <span class="c-key">Clave</span>
            <span class="c-unit">Unidad</span>
            <span class="c-price">Precio unitario (sin IVA)</span>
            <span class="c-comment">Comentarios</span>
            <span class="c-remove"></span>
          </div>

          <div class="concepto-row" *ngFor="let group of conceptos.controls; let i = index" [formGroupName]="i">
            <span class="c-pos">{{ group.get('position')?.value }}</span>

            <div class="c-qty">
              <p-inputNumber formControlName="quantity" [min]="0.01" [showButtons]="false"
                [minFractionDigits]="0" [maxFractionDigits]="2" inputStyleClass="w-full"
                [class.ng-invalid]="conceptoHasError(i, 'quantity', 'required') || conceptoHasError(i, 'quantity', 'min')"></p-inputNumber>
            </div>

            <div class="c-key">
              <p-dropdown formControlName="key" [options]="catalogOptions" optionLabel="label" optionValue="key"
                [filter]="true" filterBy="label,key" placeholder="Selecciona clave" appendTo="body" styleClass="w-full"
                [class.ng-invalid]="conceptoHasError(i, 'key', 'required')" (onChange)="onConceptoKeyChange(i)"></p-dropdown>
            </div>

            <div class="c-unit">
              <p-tag *ngIf="group.get('unit')?.value" [value]="group.get('unit')?.value"
                [severity]="group.get('unit')?.value === 'PRODUCTO' ? 'warning' : 'info'"></p-tag>
              <span *ngIf="!group.get('unit')?.value" class="unit-empty">—</span>
            </div>

            <div class="c-price">
              <div class="price-group">
                <p-inputNumber formControlName="unitPrice" mode="currency" currency="MXN" locale="es-MX" [min]="0"
                  inputStyleClass="w-full" placeholder="0.00" styleClass="price-input"
                  [class.ng-invalid]="conceptoHasError(i, 'unitPrice', 'required') || conceptoHasError(i, 'unitPrice', 'min')"></p-inputNumber>
                <button *ngIf="suggestedRent != null" type="button" class="rent-suggest-btn"
                  [pTooltip]="'Usar la renta del arrendamiento (sin IVA): ' + (suggestedRent | currency:'MXN')"
                  tooltipPosition="top" (click)="applyRentSuggestion(i)">
                  <i class="fa-solid fa-wand-magic-sparkles"></i>
                </button>
              </div>
            </div>

            <div class="c-comment">
              <input pInputText formControlName="comments" [placeholder]="defaultCommentPlaceholder" class="w-full" />
            </div>

            <div class="c-remove">
              <button type="button" class="remove-btn" pTooltip="Quitar concepto" tooltipPosition="top" (click)="removeConcepto(i)">
                <i class="fa-solid fa-trash"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ───────────── Guardar ───────────── -->
    <div class="billing-action">
      <button pButton pRipple type="submit"
        [label]="mode === 'create' ? 'Guardar facturación' : 'Actualizar facturación'"
        [icon]="mode === 'create' ? 'fa-solid fa-check' : 'fa-solid fa-pen-to-square'"
        class="p-button-success p-button-lg" [style.width]="'100%'"
        [disabled]="!isFormValid || isSubmitting" [loading]="isSubmitting"></button>
    </div>
  </form>
</div>
